import { MenuTitle } from "features/menu/components/MenuTitle";
import { H2 } from "components/atoms/H2";
import { ImageCard } from "components/molecules/ImageCard";
import { ProfileCard } from "components/molecules/ProfileCard";
import { FortuneStepGuide } from "components/organisms/FortuneStepGuide";
import { ReviewList } from "components/organisms/ReviewList";
import { SafetyPolicy } from "components/organisms/SafetyPolicy";
import { InnerOrderButton } from "features/menu/components/InnerOrderButton";
import { RelatedMenuList } from "features/menu/components/RelatedMenuList";
import useNavigation from "features/menu/hooks/useNavigation";
import { MenuTag } from "shared/components/atoms/MenuTag";
import { AchievementTable } from "shared/components/molecules/AchievementTable";
import { getMenuInfo } from "shared/features/Menu/utils/getMenuInfo";
import { Tag } from "shared/models/index";
import { MenuBannerImage } from "features/menu/components/MenuBannerImage";
import dynamic from "next/dynamic";
import Link from "next/link";
import { Paths } from "utils/routes";
import { useMenuContext } from "features/menu/hooks/useMenuContext";
import { HTMLRenderer } from "shared/components/atoms/HTMLRenderer";
import { OnlyFirstTimeMark } from "shared/features/Menu/components/OnlyFirstTimeMark";
import { CvFromChatToSubscriptionPoc } from "features/menu/components/CvFromChatToSubscriptionPoc";
import { useCvFromChatToSubscriptionPoc } from "features/menu/hooks/useCvFromChatToSubscriptionPoc";
import { TelFortuneRecommendation } from "features/menu/components/TelFortuneRecommendation";
import { RecentOrderCard } from "features/Home/components/v2/RecentOrderCard";
import { useCalledFortuneTeller } from "repositories/hooks/useCalledFortuneTeller";
import { OfficialBadge } from "shared/features/shared/components/OfficialBadge";

const CustomerHistory = dynamic(
  async () =>
    (await import("components/molecules/CustomerHistory")).CustomerHistory
);

const Carousel = dynamic(
  async () => (await import("shared/components/atoms/Carousel")).Carousel
);

const DivinationTagCardListSkeleton = dynamic(
  async () =>
    (await import("components/molecules/DivinationTagCardListSkeleton"))
      .DivinationTagCardListSkeleton
);

export const Menu = () => {
  const { menu } = useMenuContext();
  const { fortuneTeller: calledFortuneTeller } = useCalledFortuneTeller(
    menu?.fortuneTeller?.id
  );

  const { isCvFromChatToSubscriptionPocEnabled } =
    useCvFromChatToSubscriptionPoc();
  const { label } = getMenuInfo(menu, menu?.fortuneTeller);
  const { goToFortuneTeller, goToReviews } = useNavigation(menu);

  const isChatMenu = menu?.type === "chat";
  const isCustomerPresent =
    menu?.salesQuantity > 0 && menu?.purchasedUsers.length > 0;

  const renderTag = (tag: Tag) => (
    <Link href={Paths.tags.show(tag.id)}>
      <a>
        <ImageCard
          src={tag.bannerUrl}
          title={tag.name}
          width={112}
          height={160}
        />
      </a>
    </Link>
  );

  return (
    <>
      <MenuBannerImage />
      <div tw="px-5 mt-4">
        <div tw="flex items-center">
          {menu?.fortuneTeller?.isOfficial && !isChatMenu && (
            <OfficialBadge size={24} tw="w-6 h-6 mr-2" />
          )}
          {menu?.isOnlyFirstTime && (
            <OnlyFirstTimeMark tw="mr-2 text-xs leading-none h-6 px-3" />
          )}
          <MenuTag tw="text-xs leading-none h-6 px-1.5">{label}</MenuTag>
        </div>
        <MenuTitle tw="mt-4">{menu?.title}</MenuTitle>
        {isCustomerPresent && (
          <CustomerHistory
            tw="mt-3"
            users={menu?.purchasedUsers}
            salesQuantity={menu?.salesQuantity}
          />
        )}
        <AchievementTable tw="mt-4" data={menu?.achievement} />
        <H2 tw="mt-8" readMore onClick={goToFortuneTeller}>
          占い師プロフィール
        </H2>
        <ProfileCard tw="mt-6" fortuneTeller={menu?.fortuneTeller} />
        {calledFortuneTeller && (
          <RecentOrderCard fortuneTeller={calledFortuneTeller} tw="mt-5" />
        )}

        {isChatMenu && menu?.fortuneTeller.telAcceptFlag ? (
          <TelFortuneRecommendation
            tw="mt-6"
            fortuneTeller={menu?.fortuneTeller}
          />
        ) : (
          <></>
        )}
      </div>
      <H2 tw="px-5 mt-8">🔮 使用する占術</H2>
      {menu?.divinationTags ? (
        <Carousel
          tw="mt-6"
          data={menu?.divinationTags}
          renderItem={renderTag}
        />
      ) : (
        <DivinationTagCardListSkeleton />
      )}
      <div tw="px-5">
        <H2 tw="mt-8">鑑定メニュー内容</H2>
        <HTMLRenderer tw="font-base font-light text-sm leading-6 tracking-[.41px] whitespace-pre-wrap mt-5">
          {menu?.content}
        </HTMLRenderer>
        <H2 tw="mt-8">先生からのお願い</H2>
        <HTMLRenderer tw="font-base font-light text-sm leading-6 tracking-[.41px] whitespace-pre-wrap mt-5">
          {menu?.note}
        </HTMLRenderer>
      </div>
      {isCvFromChatToSubscriptionPocEnabled ? (
        <CvFromChatToSubscriptionPoc />
      ) : (
        <InnerOrderButton tw="mt-8" />
      )}
      <div tw="px-5">
        <SafetyPolicy tw="mt-14" />
        <H2 tw="mt-10">{isChatMenu ? "チャット" : "電話"}鑑定の流れ</H2>
        <FortuneStepGuide tw="m-4" isTel={!isChatMenu} />
        <ReviewList
          tw="my-16"
          reviews={menu?.reviews}
          goToReviews={goToReviews}
        />
      </div>
      <RelatedMenuList />
    </>
  );
};
